/* @flow */
/**
 * Flow type: all possible flow types.
 * The types contained in this file should not be changed lightly as they are stored on the CMR (kwanza) and user by other software (e.g. Manatee) which expects them to look a certain way.
 *
 * NB: documentation.js can't parse `|` ([see issue](https://github.com/documentationjs/documentation/issues/589)). Here's the raw type:
 * ```
 * type FlowType = 'ACTION' | 'STATE' | 'MODULE'
 * ```
 * @typedef { string } FlowType
 * @memberof FlowService
 */
import _ from 'lodash'
import Typenames from './Typenames'
import { ActionTypes, store } from 'store'
import { Toast, User } from 'services'
import { i18n } from 'i18n'
import { Application } from '.'
import kwanza from './Http'

type FlowType = 'STATE' | 'ACTION' | 'MODULE'
/**
 * Input: flow i/o.
 * @typedef { Object } FlowInput
 * @memberof FlowService
 */
type FlowInput = {
  name: string,
  type: string
}

type ContextTrigger = {
  subject: string,
  regexpNew: string,
  regexpOld: string
}

type LifeCycleEvent = 'ATTACHED'

type LifeCycleTrigger = {
  event: LifeCycleEvent
}

type ExternalTrigger = {}

type ServiceTrigger = {
  serviceType: string,
  configuration: Object
}

/** FlowTrigger: A trigger for a flow
 */
type FlowTrigger = {
  context?: Array<ContextTrigger>,
  lifecycle?: Array<LifeCycleTrigger>,
  external?: Array<ExternalTrigger>,
  services?: Array<ServiceTrigger>
}

type ActionType = 'GET' | 'SET' | 'MAP' | ''

/**
 * Type Config: flow configuration for a type.
 * NB: documentation.js can't parse `|` ([see issue](https://github.com/documentationjs/documentation/issues/589)). Here's the raw type:
 * ```
 * type FlowTypeConfig = {
 *   [type: FlowType]: {
 *     action?: ActionType,
 *     subject?: string,
 *     subjectPrefix?: string
 *   }
 * }
 * ```
 * @typedef { Object } FlowTypeConfig
 * @memberof FlowService
 */

type FlowTypeConfig = {
  subject?: string,
  action?: ActionType,
  subjectPrefix?: string,
  triggers?: FlowTrigger
}

/**
 * Flow: a flow contains configuration, code and can reference fields. It can also be grouped.
 * @typedef { Object } Flow
 * @memberof FlowService
 */
export type Flow = {
  id?: string,
  name: string,
  version?: string,
  inputs?: Array<FlowInput>,
  outputs?: Array<FlowInput>,
  application: string,
  code: string,
  editorCode: string,
  language: string,
  sourcemap: string,
  blocklyXml?: string,
  maintainer: ?string,
  type: FlowType,
  typeConfig: FlowTypeConfig,
  anchors?: Array<string>,
  usesfields?: Array<string>,
  usesFlows?: Array<string>,
  hideFromUser?: boolean,
  compatibleVersion?: boolean,
  precompile?: boolean,
  restoreFocus?: boolean,
  isMandatory?: boolean,
  publication?: Object
}

const defaultFlow = {
  name: '',
  inputs: [],
  outputs: [],
  application: '',
  code: '',
  blocklyXml: '',
  maintainer: '',
  type: 'STATE',
  typeConfig: {},
  anchors: [],
  usesfields: [],
  usesFlows: [],
  compatibleVersion: '1.28',
  hideFromUser: false,
  precompile: true,
  restoreFocus: false,
  isMandatory: false
}

/**
 * Flow typename
 * @private
 * @memberof FlowService
 */
const TYPENAME = Typenames.FLOW

/**
 * Map containing configuration for all flow types.
 * See more [in the wiki](https://gitlab.com/sirenia/cuesta/wikis/flowtypeconfig) and {@link #flow|Flow}.
 *
 * @instance
 * @memberof FlowService
 */
const defaultFlowTypeConfig = Object.freeze({
  STATE: {
    subject: '',
    action: ''
  },
  ACTION: {
    subjectPrefix: '',
    triggers: {
      context: [],
      external: [],
      lifecycle: []
    }
  },
  MODULE: {
    subject: ''
  }
})

const supportedLanguages = Object.freeze({
  ES5: 'es5',
  MODERN: 'ts'
})

const flowTypeIcons = {
  STATE: 'handshake outline',
  ACTION: 'rocket',
  MODULE: 'cloud download'
}

/**
 * Validates a passed object against the required Flow type properties
 * @function
 * @memberof FlowService
 *
 * @return { boolean } The information if the passed object contains all the required Flow properties
 */
const validateFlowObject = (flow?: Object): boolean => {
  return [
    'name',
    'inputs',
    'outputs',
    'application',
    'code',
    'type',
    'typeConfig',
    'icon',
    'usesfields'
  ].every(k => _.has(flow, k))
}

const mapFlow = flow => {
  return { ...defaultFlow, ...flow }
}

// Finds flows that has name or subjectPrefix matching the given flow, grouped by flows within same
// app and flows from other apps
const findConflictingFlows = (flow: Flow): Promise<Object> => {
  function findConflicts() {
    return readAllFlows()
      .then(allFlows => {
        // Reject flows with same id if flow has an id as well as flows without matching subject/name
        return allFlows.filter(f => {
          const fSubject = (f.typeConfig && f.typeConfig.subjectPrefix) || ''
          const flowSubject = (flow.typeConfig && flow.typeConfig.subjectPrefix) || ''
          const nameMatch = f.name && (f.name === flow.name || f.name === flowSubject)
          const subjectMatch = fSubject && (fSubject === flow.name || fSubject === flowSubject)
          return (!flow.id || flow.id !== f.id) && (nameMatch || subjectMatch)
        })
      })
      .then(filteredMatches => {
        return _.groupBy(filteredMatches, f =>
          f.application === flow.application ? 'sameApp' : 'otherApp'
        )
      })
      .then(({ sameApp, otherApp }) => ({ sameApp: sameApp || [], otherApp: otherApp || [] }))
  }

  if (flow.id) {
    // Bypass expensive conflict check if name and subject is unchanged
    return readFlow(flow.id)
      .then(oldFlow => {
        const fSubject = flow.typeConfig && flow.typeConfig.subjectPrefix
        const oldFSubject = oldFlow.typeConfig && oldFlow.typeConfig.subjectPrefix
        if (oldFlow.name === flow.name && fSubject === oldFSubject)
          return { sameApp: [], otherApp: [] }
        else return findConflicts()
      })
      .catch(() => findConflicts())
  } else {
    return findConflicts()
  }
}

const checkConflictingFlows = (flow: Flow): Promise<Object> => {
  return findConflictingFlows(flow).then(conflictingFlows => {
    if (conflictingFlows.sameApp.length > 0) {
      Toast.warning(i18n.t('ERROR_CONFLICTING_FLOW'), {
        remove: 5000,
        text: i18n.t('ERROR_CONFLICTING_FLOW_SAME_APP', {
          conflictingFlow: conflictingFlows.sameApp[0].name
        })
      })
      throw Error(`Conflicting flow ${conflictingFlows.sameApp[0].name} exists in same app`)
    }
    if (conflictingFlows.otherApp.length > 0) {
      return Application.readApp(conflictingFlows.otherApp[0].application).then(conflictApp => {
        if (
          !window.confirm(
            i18n.t('WARN_CONFLICTING_FLOW_OTHER_APP', {
              conflictingApp: conflictApp.name,
              conflictingFlow: conflictingFlows.otherApp[0].name
            })
          )
        )
          throw Error(
            `Conflicting flow ${conflictingFlows.otherApp[0].name} exists in app ${conflictApp.name}`
          )
      })
    }
  })
}

/**
 * Creates a new flow.
 * @function
 * @memberof FlowService
 *
 * @return { Promise<string> } The id of the added flow.
 */
const createFlow = (flow: Flow): Promise<PostResponse> => {
  return checkConflictingFlows(flow).then(() => {
    return store
      .dispatch(ActionTypes.CREATE, {
        typename: TYPENAME,
        item: mapFlow(flow)
      })
      .then(r => {
        User.addEdit({
          type: TYPENAME,
          id: r.id,
          name: flow.name,
          routeParams: { flowId: r.id, appId: flow.application, mode: 'code' }
        })
        return r
      })
  })
}

/**
 * Reads a flow, flattening 'descriptive'.
 * @function
 * @memberof FlowService
 *
 * @return { Promise<Flow> } The flow.
 */
const readFlow = (id: string): Promise<Flow> => {
  return store
    .dispatch(ActionTypes.GET_BY_ID, { typename: TYPENAME, id: id })
    .then(flow => mapFlow(flow))
}

/**
 * Updates a flow.
 * @function
 * @memberof FlowService
 *
 * @return { Promise<any> }
 */
const updateFlow = (id: string, flow: Flow, fromHub: boolean): Promise<PostResponse> => {
  // Track local changes if this is a downloaded/published flow
  if (!fromHub && flow.publication) {
    flow.publication.hasLocalChanges = true
  } else if (fromHub && flow.publication) {
    flow.publication.hasLocalChanges = false
  }

  return checkConflictingFlows(flow).then(() => {
    return store
      .dispatch(ActionTypes.UPDATE, {
        typename: TYPENAME,
        id: id,
        item: flow
      })
      .then(r => {
        User.addEdit({
          type: TYPENAME,
          id,
          name: flow.name,
          routeParams: { flowId: id, appId: flow.application, mode: 'code' }
        })
        return r
      })
  })
}

/**
 * Deletes a flow.
 * @function
 * @memberof FlowService
 *
 * @return { Promise<any> }
 */
const deleteFlow = (id: string): Promise<any> => {
  return store.dispatch(ActionTypes.DELETE, { typename: TYPENAME, id: id })
}

/**
 * Reads all flows flattening descriptive.
 * @function
 * @memberof FlowService
 *
 * @return { Observable<Array<Flow>> } All the flows.
 */
const readAllFlows = (): Promise<Array<Flow>> => {
  return store.dispatch(ActionTypes.GET, TYPENAME).then(flows => flows.map(f => mapFlow(f)))
}

/**
 * Reads all flows for a given app, flattening descriptive.
 * @function
 * @memberof FlowService
 *
 * @return { Observable<Array<Flow>> } Flows for given app.
 */
const readFlowsForApp = (appId: string): Promise<Array<Flow>> => {
  return store
    .dispatch(ActionTypes.GET_FOR_APP, { typename: TYPENAME, appId: appId })
    .then(flows => flows.map(f => mapFlow(f)))
}

/**
 * Reads all flows for a given maintainer (user id), flattening descriptive.
 * @function
 * @memberof FlowService
 *
 * @return { Observable<Array<Flow>> } Flow for given maintainer.
 */
const readFlowsForMaintainer = (userId: string): Promise<Array<Flow>> => {
  return store
    .dispatch(ActionTypes.GET_FOR_MAINTAINER, { typename: TYPENAME, userId: userId })
    .then(flows => flows.map(f => mapFlow(f)))
}

/**
 * Read flows for given application, which use given fields.
 *
 * @function
 * @memberof FlowService
 *
 * @return { Promise<Array<string>> }
 */
const readFlowsForAppThatUseField = (appId: string, fieldId: string): Promise<Array<Flow>> => {
  return readFlowsForApp(appId).then(flows => {
    return _.filter(
      flows,
      flow => flow.application === appId && _.includes(flow.usesfields, fieldId)
    )
  })
}

/**
 * TODO: update this method to get deep contents (kwanza#41/42) instead of looping through all flows.
 *
 * Reads all flows that use a subject as input / output.
 * @function
 * @memberof FlowService
 *
 * @return { Promise<Array<Flow>> } Flows that the subject is used in as input / output.
 */
const readFlowsForSubject = (subjectId: string): Promise<Array<Flow>> => {
  return readAllFlows().then(flows => {
    return flows.filter(f => {
      const subjectInInputs = f.inputs ? f.inputs.find(i => i.type === subjectId) : null
      const subjectInOutpts = f.outputs ? f.outputs.find(o => o.type === subjectId) : null
      return subjectInInputs || subjectInOutpts
    })
  })
}

/**
 * SavedSearch: A representation of a saved flow search
 * @typedef { Object } SavedSearch
 * @memberof FlowService
 */
type SavedSearch = {
  name: string,
  groupBy: string,
  filterWith: string,
  columns: string,
  sortBy: string
}

/**
 * Get all saved searches for flows
 * @function
 * @memberof FlowService
 *
 * @return { Promise<Array<SavedSearch>> } All saved searches.
 */
const getSearches = (): Promise<Array<SavedSearch>> => kwanza.getAll('flow_search')

/**
 * Update an existing search or create a new
 * @function
 * @memberof FlowService
 */
const saveSearch = (id: string, version: string, search: SavedSearch) =>
  kwanza.updateItem('flow_search', id, { ...search, version: version })

/**
 * Delete a saved search
 * @function
 * @memberof FlowService
 */
const deleteSearch = (id: string) => kwanza.deleteItem('flow_search', id)

const self = {
  getSearches,
  saveSearch,
  deleteSearch,
  createFlow,
  readFlow,
  readFlowsForApp,
  readFlowsForMaintainer,
  updateFlow,
  deleteFlow,
  readAllFlows,
  readFlowsForAppThatUseField,
  validateFlowObject,
  readFlowsForSubject,
  flowTypeIcons,
  defaultFlowTypeConfig,
  supportedLanguages
}

/**
 * Flow service: CRUD interface for flows.
 * @module FlowService
 * @namespace FlowService
 * @version 1.0.0
 */
export default self
